import { Injectable, inject } from '@angular/core';
import { CommonDataService } from './common-data.service';
import { OnboardingStateKey } from '@shared/interfaces/onboarding-state.interface';
import { PATHS, FLOW_TYPE, FlowType, FIRST_INDEX, NOT_FOUND_INDEX, INDEX_OFFSET } from '@constants';
import { isNullOrUndefined } from '@utils/predicates';

// =============================================================================
// Definicion de pasos del onboarding
//
// Cada paso tiene:
//   - path: ruta del paso
//   - requires: claves del estado que deben existir para acceder
//
// Para agregar un nuevo paso:
//   1. Agregar la ruta a PATHS en constants.ts
//   2. Agregar el paso al flujo correspondiente en FLOW_DEFINITIONS
//   3. Si el paso es compartido entre flujos, agregarlo en todos los que aplique
// =============================================================================

export interface OnboardingStep {
  path: string;
  requires: OnboardingStateKey[];
}

/**
 * Pasos compartidos por todos los flujos.
 * Se reutilizan en las definiciones de cada flujo.
 */
type SharedStepKey = 'questions' | 'testResult' | 'portfolioDetail' | 'welcome' | 'accountOwner' | 'contactData' | 'videoIdentification' | 'personalData' | 'addressData' | 'activityData' | 'investmentKnowledge' | 'financialSituation' | 'pepData' | 'signContract' | 'createPassword';

const SHARED_STEPS: Record<SharedStepKey, OnboardingStep> = {
  questions: { path: PATHS.questions, requires: [] },
  testResult: { path: PATHS.testResult, requires: ['survey'] },
  portfolioDetail: { path: PATHS.portfolioDetail, requires: ['survey', 'suitability'] },
  welcome: { path: PATHS.welcome, requires: ['survey', 'suitability'] },
  accountOwner: { path: PATHS.accountOwner, requires: ['survey', 'suitability'] },
  contactData: { path: PATHS.contactData, requires: ['survey', 'suitability', 'accountOwner'] },
  videoIdentification: { path: PATHS.videoIdentification, requires: ['survey', 'suitability', 'accountOwner'] },
  personalData: { path: PATHS.personalData, requires: ['survey', 'suitability', 'accountOwner'] },
  addressData: { path: PATHS.addressData, requires: ['survey', 'suitability', 'accountOwner'] },
  activityData: { path: PATHS.activityData, requires: ['survey', 'suitability', 'accountOwner'] },
  investmentKnowledge: { path: PATHS.investmentKnowledge, requires: ['survey', 'suitability', 'accountOwner'] },
  financialSituation: { path: PATHS.financialSituation, requires: ['survey', 'suitability', 'accountOwner'] },
  pepData: { path: PATHS.pepData, requires: ['survey', 'suitability', 'accountOwner'] },
  signContract: { path: PATHS.signContract, requires: ['survey', 'suitability', 'accountOwner'] },
  createPassword: { path: PATHS.createPassword, requires: ['survey', 'suitability', 'accountOwner'] }
};

/**
 * Definicion de flujos.
 *
 * Mono-titular: flujo estandar de un solo titular.
 * Multi-titular: repite ciertos pasos para el segundo titular.
 * Recovery: entra en un paso intermedio segun el estado del backend.
 *
 * Los pasos de multi/recovery que aun no existen como componentes
 * se iran agregando a medida que se construyan.
 */
export const FLOW_DEFINITIONS: Record<FlowType, OnboardingStep[]> = {
  [FLOW_TYPE.MONO]: [
    SHARED_STEPS.questions,
    SHARED_STEPS.testResult,
    SHARED_STEPS.portfolioDetail,
    SHARED_STEPS.welcome,
    SHARED_STEPS.accountOwner,
    SHARED_STEPS.contactData,
    SHARED_STEPS.videoIdentification,
    SHARED_STEPS.personalData,
    SHARED_STEPS.addressData,
    SHARED_STEPS.activityData,
    SHARED_STEPS.investmentKnowledge,
    SHARED_STEPS.financialSituation,
    SHARED_STEPS.pepData,
    SHARED_STEPS.signContract,
    SHARED_STEPS.createPassword,
    { path: PATHS.completed, requires: ['survey', 'suitability', 'accountOwner'] }
    // { path: PATHS.videoId, requires: ['survey', 'suitability', 'contactData'] },
    // { path: PATHS.personalData, requires: ['survey', 'suitability', 'contactData'] },
    // { path: PATHS.activity, requires: ['survey', 'suitability', 'personalData'] },
    // { path: PATHS.signContract, requires: ['survey', 'suitability', 'activity'] },
    // { path: PATHS.credentials, requires: ['survey', 'suitability', 'signContract'] },
    // { path: PATHS.completed, requires: ['survey', 'suitability', 'credentials'] },
  ],
  [FLOW_TYPE.MULTI]: [
    SHARED_STEPS.questions,
    SHARED_STEPS.testResult,
    SHARED_STEPS.portfolioDetail,
    SHARED_STEPS.welcome,
    SHARED_STEPS.accountOwner,
    SHARED_STEPS.contactData
    // { path: PATHS.contactDataSecond, requires: ['survey', 'suitability', 'contactData'] },
    // { path: PATHS.personalData, requires: ['survey', 'suitability', 'contactDataSecond'] },
    // { path: PATHS.personalDataSecond, requires: ['survey', 'suitability', 'personalData'] },
    // ...
  ],
  [FLOW_TYPE.RECOVERY]: [
    SHARED_STEPS.questions,
    SHARED_STEPS.testResult,
    SHARED_STEPS.portfolioDetail,
    SHARED_STEPS.welcome,
    SHARED_STEPS.accountOwner,
    SHARED_STEPS.contactData,
    SHARED_STEPS.videoIdentification,
    SHARED_STEPS.personalData,
    SHARED_STEPS.addressData,
    SHARED_STEPS.activityData,
    SHARED_STEPS.investmentKnowledge,
    SHARED_STEPS.financialSituation,
    SHARED_STEPS.pepData,
    SHARED_STEPS.signContract,
    SHARED_STEPS.createPassword,
    { path: PATHS.completed, requires: ['survey', 'suitability', 'accountOwner'] }
  ]
};

/** Flujo por defecto cuando aun no se ha seleccionado tipo */
const DEFAULT_FLOW = FLOW_TYPE.MONO;

@Injectable({ providedIn: 'root' })
export class OnboardingFlowService {
  private readonly commonData = inject(CommonDataService);

  /**
   * Retorna el flujo activo segun el estado global.
   * Si no hay flowType seleccionado, usa el flujo por defecto.
   */
  getActiveFlow(): OnboardingStep[] {
    const flowType = this.commonData.get('flowType');
    let type: FlowType = DEFAULT_FLOW;
    if (!isNullOrUndefined(flowType)) {
      type = flowType;
    }
    return FLOW_DEFINITIONS[type];
  }

  /**
   * Verifica si el usuario puede acceder a un paso.
   */
  canAccessStep(path: string): boolean {
    const steps = this.getActiveFlow();
    const step = steps.find((s) => s.path === path);

    if (!step) {
      return false;
    }

    return step.requires.every((key) => !isNullOrUndefined(this.commonData.get(key)));
  }

  /**
   * Retorna la ruta del ultimo paso accesible en el flujo activo.
   */
  getLastAccessiblePath(): string {
    const steps = this.getActiveFlow();
    let lastAccessible = steps[0].path;

    for (const step of steps) {
      const canAccess = step.requires.every((key) => !isNullOrUndefined(this.commonData.get(key)));
      if (canAccess) {
        lastAccessible = step.path;
      } else {
        break;
      }
    }

    return lastAccessible;
  }

  /**
   * Retorna la ruta del paso anterior al indicado.
   */
  getPreviousPath(currentPath: string): string {
    const steps = this.getActiveFlow();
    const currentIndex = steps.findIndex((s) => s.path === currentPath);
    const isFirstOrNotFound = currentIndex <= FIRST_INDEX;

    if (isFirstOrNotFound) {
      return steps[FIRST_INDEX].path;
    }

    return steps[currentIndex - INDEX_OFFSET].path;
  }

  /**
   * Retorna la ruta del paso siguiente al indicado.
   * Si no hay siguiente, retorna null.
   */
  getNextPath(currentPath: string): string | null {
    const steps = this.getActiveFlow();
    const currentIndex = steps.findIndex((s) => s.path === currentPath);
    const isNotFound = currentIndex === NOT_FOUND_INDEX;
    const isLastStep = currentIndex >= steps.length - INDEX_OFFSET;

    if (isNotFound || isLastStep) {
      return null;
    }

    return steps[currentIndex + INDEX_OFFSET].path;
  }
}
